import {
	Alert,
	AlertDescription,
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Separator,
} from "@monbits/ui";
import { AlertTriangle, Copy, Download, Eye, EyeOff } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { TwoFactorVerification } from "src/components/two-factor/two-factor-verification";
import walletProvider, {
	type WalletBackupResponse,
} from "src/providers/wallet-provider";

interface WalletBackupDialogProps {
	walletId: number | null;
	walletAddress: string;
	onClose: () => void;
}

type DialogStep = "verify" | "display";

const WalletBackupDialog: React.FC<WalletBackupDialogProps> = ({
	walletId,
	walletAddress,
	onClose,
}) => {
	const [step, setStep] = React.useState<DialogStep>("verify");
	const [backupData, setBackupData] =
		React.useState<WalletBackupResponse | null>(null);
	const [showPrivateKey, setShowPrivateKey] = React.useState(false);

	// Reset state when dialog opens/closes
	React.useEffect(() => {
		if (walletId !== null) {
			setStep("verify");
			setBackupData(null);
			setShowPrivateKey(false);
		}
	}, [walletId]);

	const handleVerify = async (code: string): Promise<boolean> => {
		if (!walletId) return false;

		try {
			const data = await walletProvider.downloadWalletBackup(walletId, code);
			setBackupData(data);
			setStep("display");
			toast.success("Wallet backup retrieved successfully");
			return true;
		} catch (error: unknown) {
			console.error("Failed to download wallet backup:", error);

			const err = error as { response?: { status?: number } };
			if (err.response?.status === 403) {
				toast.error("Invalid 2FA code", {
					description: "Please check your authenticator app and try again",
				});
			} else if (err.response?.status === 404) {
				toast.error("Wallet not found");
			} else {
				toast.error("Failed to download wallet backup");
			}
			return false;
		}
	};

	const handleCopy = async (text: string, label: string) => {
		try {
			await navigator.clipboard.writeText(text);
			toast.success(`${label} copied to clipboard`);
		} catch (error) {
			console.error("Failed to copy:", error);
			toast.error("Failed to copy to clipboard");
		}
	};

	const handleDownloadJSON = () => {
		if (!backupData) return;

		const jsonString = JSON.stringify(backupData, null, 2);
		const blob = new Blob([jsonString], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `wallet-backup-${backupData.address}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);

		toast.success("Backup downloaded as JSON");
	};

	const handleClose = () => {
		// Clear sensitive data from memory
		setBackupData(null);
		setShowPrivateKey(false);
		setStep("verify");
		onClose();
	};

	const handleCancel = () => {
		handleClose();
	};

	return (
		<Dialog
			open={walletId !== null}
			onOpenChange={(open) => !open && handleClose()}
		>
			<DialogContent className="sm:max-w-[600px]">
				{step === "verify" && (
					<>
						<DialogHeader>
							<DialogTitle>Download Wallet Backup</DialogTitle>
							<DialogDescription>
								Two-factor authentication is required to download private keys
								for{" "}
								<code className="text-xs bg-muted px-1 py-0.5 rounded">
									{walletAddress.slice(0, 10)}...{walletAddress.slice(-8)}
								</code>
							</DialogDescription>
						</DialogHeader>

						<Alert variant="destructive">
							<AlertTriangle className="h-4 w-4" />
							<AlertDescription>
								This will expose your wallet's private keys. Handle with extreme
								care.
							</AlertDescription>
						</Alert>

						<TwoFactorVerification
							onVerify={handleVerify}
							onCancel={handleCancel}
							showBackupOption={true}
							description="Enter the code from your authenticator app or use a backup code"
							submitLabel="Verify & Download"
							cancelLabel="Cancel"
						/>
					</>
				)}

				{step === "display" && backupData && (
					<>
						<DialogHeader>
							<DialogTitle>Wallet Backup</DialogTitle>
							<DialogDescription>
								Private keys retrieved successfully. Store them securely.
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4">
							<Alert variant="destructive">
								<AlertTriangle className="h-4 w-4" />
								<AlertDescription>
									<strong>Never share these keys.</strong> Anyone with access
									can steal your funds.
								</AlertDescription>
							</Alert>

							<div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
								{/* Address */}
								<div className="space-y-2">
									<Label className="text-xs font-medium text-muted-foreground">
										Address
									</Label>
									<div className="flex gap-2">
										<Input
											value={backupData.address}
											readOnly
											className="font-mono text-xs"
										/>
										<Button
											size="icon"
											variant="outline"
											onClick={() => handleCopy(backupData.address, "Address")}
											title="Copy address"
										>
											<Copy className="h-4 w-4" />
										</Button>
									</div>
								</div>

								{/* Public Key */}
								<div className="space-y-2">
									<Label className="text-xs font-medium text-muted-foreground">
										Public Key
									</Label>
									<div className="flex gap-2">
										<Input
											value={backupData.publicKey}
											readOnly
											className="font-mono text-xs"
										/>
										<Button
											size="icon"
											variant="outline"
											onClick={() =>
												handleCopy(backupData.publicKey, "Public key")
											}
											title="Copy public key"
										>
											<Copy className="h-4 w-4" />
										</Button>
									</div>
								</div>

								<Separator />

								{/* Private Key */}
								<div className="space-y-2">
									<Label className="text-xs font-medium text-destructive">
										Private Key
									</Label>
									<div className="flex gap-2">
										<Input
											type={showPrivateKey ? "text" : "password"}
											value={backupData.privateKey}
											readOnly
											className="font-mono text-xs"
										/>
										<Button
											size="icon"
											variant="outline"
											onClick={() => setShowPrivateKey(!showPrivateKey)}
											title={
												showPrivateKey ? "Hide private key" : "Show private key"
											}
										>
											{showPrivateKey ? (
												<EyeOff className="h-4 w-4" />
											) : (
												<Eye className="h-4 w-4" />
											)}
										</Button>
										<Button
											size="icon"
											variant="outline"
											onClick={() =>
												handleCopy(backupData.privateKey, "Private key")
											}
											title="Copy private key"
										>
											<Copy className="h-4 w-4" />
										</Button>
									</div>
									<p className="text-xs text-muted-foreground">
										Click the eye icon to reveal the private key
									</p>
								</div>

								<Separator />

								{/* Metadata */}
								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<Label className="text-xs font-medium text-muted-foreground">
											Blockchain
										</Label>
										<p className="text-sm mt-1">{backupData.blockchain}</p>
									</div>
									<div>
										<Label className="text-xs font-medium text-muted-foreground">
											Type
										</Label>
										<p className="text-sm mt-1 capitalize">{backupData.type}</p>
									</div>
								</div>
							</div>
						</div>

						<DialogFooter className="gap-2">
							<Button variant="outline" onClick={handleClose}>
								Close
							</Button>
							<Button onClick={handleDownloadJSON} variant="default">
								<Download className="mr-2 h-4 w-4" />
								Download JSON
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default WalletBackupDialog;
